import { useState } from "react";

import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import { Autocomplete, Button, Stack, TextField } from "@mui/material";
import { useConfirmationDialogContext } from "@shared";

import { ClientVolunteerAssignmentResponse, VolunteerSearchResult } from "@/api";
import { useTranslation } from "@/translations";

interface Props {
  assignment: ClientVolunteerAssignmentResponse;
  volunteerOptions: VolunteerSearchResult[];
  onAssign: (volunteerId: string) => void;
  onRevoke: () => void;
  isSubmitting: boolean;
}

function formatVolunteerLabel(volunteer: VolunteerSearchResult): string {
  const name = volunteer.firstName ? `${volunteer.lastName}, ${volunteer.firstName}` : volunteer.lastName;
  return `${name} (${volunteer.username})`;
}

/**
 * The whole assignment UI: one dropdown seeded with the currently released volunteer, plus one button
 * whose meaning follows the assignment and the selection — four states:
 *
 * | Assigned | Selection      | Button                              |
 * |----------|----------------|-------------------------------------|
 * | no       | none           | "Akt freigeben", disabled           |
 * | no       | someone        | "Akt freigeben"                     |
 * | yes      | untouched/none | "Freigabe zurücknehmen"             |
 * | yes      | someone else   | "Freigabe übertragen"               |
 *
 * Replacing goes through a single backend transaction that revokes and re-releases. Both
 * replace and revoke take an assigned volunteer's access away, so both go behind a confirmation.
 *
 * Remount this component (keyed on the assigned volunteer) to reseed the dropdown after a mutation.
 */
export function VolunteerAssignmentCard({ assignment, volunteerOptions, onAssign, onRevoke, isSubmitting }: Props) {
  const { t } = useTranslation();
  const { showConfirmationDialog } = useConfirmationDialogContext();

  // undefined = untouched, so the dropdown follows the assignment; null = explicitly cleared.
  const [selectedVolunteerId, setSelectedVolunteerId] = useState<string | null | undefined>(undefined);

  // A volunteer soft-deleted after being assigned is still returned by the assignment endpoint but no
  // longer appears in the search results. Keep them selectable so the release can still be revoked.
  // Volunteers are only soft-deleted, so the backend always resolves the name fields; the coalescing
  // only satisfies the non-null option type (a truly missing row would label the option "(id)").
  const assignedButUnlisted: VolunteerSearchResult | null =
    assignment.volunteerId && !volunteerOptions.some((v) => v.volunteerId === assignment.volunteerId)
      ? {
          volunteerId: assignment.volunteerId,
          username: assignment.username ?? assignment.volunteerId,
          lastName: assignment.lastName ?? "",
          firstName: assignment.firstName,
          email: null,
          phone: null,
        }
      : null;
  const options = assignedButUnlisted ? [assignedButUnlisted, ...volunteerOptions] : volunteerOptions;

  const targetVolunteerId = selectedVolunteerId === undefined ? assignment.volunteerId : selectedVolunteerId;
  const selectedVolunteer = options.find((v) => v.volunteerId === targetVolunteerId) ?? null;
  // Whose access the revoke dialog is about — not necessarily the selection, since clearing the
  // dropdown also means revoke.
  const assignedVolunteer = options.find((v) => v.volunteerId === assignment.volunteerId) ?? null;

  // Revoking is only on the table once something is actually released — otherwise an untouched
  // dropdown on an unreleased file would offer to withdraw a release that never happened.
  // Clearing the dropdown means the same thing as leaving the assigned volunteer in place: revoke.
  const isRevoke =
    assignment.volunteerId !== null && (targetVolunteerId === null || targetVolunteerId === assignment.volunteerId);
  const isReplace = !isRevoke && assignment.volunteerId !== null;

  const handleClick = () => {
    if (isRevoke) {
      showConfirmationDialog({
        dialogTitleText: t("volunteerAssignment.revokeConfirm.title"),
        dialogText: t("volunteerAssignment.revokeConfirm.text", {
          // assignedButUnlisted guarantees the assigned volunteer is always in options during a revoke.
          name: assignedVolunteer ? formatVolunteerLabel(assignedVolunteer) : "",
        }),
        discardText: t("common.cancel"),
        agreeText: t("volunteerAssignment.revokeConfirm.confirm"),
        agreeColor: "warning",
        agreeIcon: <LockOpenOutlinedIcon />,
        onClose: (confirmed) => confirmed && onRevoke(),
      });
      return;
    }

    if (!selectedVolunteer) {
      return;
    }

    if (!isReplace) {
      onAssign(selectedVolunteer.volunteerId);
      return;
    }

    showConfirmationDialog({
      dialogTitleText: t("volunteerAssignment.replaceConfirm.title"),
      dialogText: t("volunteerAssignment.replaceConfirm.text", { name: formatVolunteerLabel(selectedVolunteer) }),
      discardText: t("common.cancel"),
      agreeText: t("volunteerAssignment.replaceConfirm.confirm"),
      agreeColor: "warning",
      agreeIcon: <SwapHorizOutlinedIcon />,
      onClose: (confirmed) => confirmed && onAssign(selectedVolunteer.volunteerId),
    });
  };

  const buttonLabel = isRevoke
    ? t("volunteerAssignment.revokeButton")
    : isReplace
      ? t("volunteerAssignment.replaceButton")
      : t("volunteerAssignment.assignButton");

  const buttonIcon = isRevoke ? <LockOpenOutlinedIcon /> : isReplace ? <SwapHorizOutlinedIcon /> : <LockOutlinedIcon />;

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Autocomplete
        options={options}
        value={selectedVolunteer}
        onChange={(_, value) => setSelectedVolunteerId(value?.volunteerId ?? null)}
        getOptionLabel={formatVolunteerLabel}
        isOptionEqualToValue={(option, value) => option.volunteerId === value.volunteerId}
        sx={{ flex: 1, maxWidth: 640 }}
        renderInput={(params) => <TextField {...params} label={t("volunteerAssignment.volunteerLabel")} size="small" />}
      />

      <Button
        variant={isRevoke ? "outlined" : "contained"}
        color={isRevoke ? "warning" : "primary"}
        startIcon={buttonIcon}
        disabled={isSubmitting || (!isRevoke && !selectedVolunteer)}
        onClick={handleClick}
      >
        {buttonLabel}
      </Button>
    </Stack>
  );
}
